// ─────────────────────────────────────────────
//  AvailabilitySection — at-a-glance on-call / going-home view (D-02, D-03)
//  Left: all doctors by call-order (ascending — #1 on call first).
//  Right: all CRNAs by go-home time (soonest to leave at top) with the
//  GoHomeAlert badge when within the alert window. Sorting comes from
//  src/domain/schedule.js — no inline sort/threshold logic here.
// ─────────────────────────────────────────────
import React from "react";
import tokens from "../theme/tokens";
import Card from "./Card";
import GoHomeAlert from "./GoHomeAlert";
import { useIsMobile } from "../hooks/useIsMobile";
import { sortDoctorsByCallOrder, sortCrnasByGoHome, callOrderLabel } from "../domain/schedule";

const { color, font, space, radius } = tokens;

const AicPill = () => <span style={st.aicPill}>AIC</span>;

const DepartedName = ({ name }) => (
  <span style={st.departedName}>
    <s>{name}</s> <span style={st.departedLabel}>(Departed)</span>
  </span>
);

export default function AvailabilitySection({
  doctors = [],
  crnas = [],
  aicId = null,
  now,
  departed = {}, // roster departed map: memberId → boolean
  renderActions, // optional: (person) => node — e.g. the DepartureFlow button
}) {
  const isMobile = useIsMobile(640);

  const sortedDoctors = sortDoctorsByCallOrder(doctors);
  const sortedCrnas = sortCrnasByGoHome(crnas);

  return (
    <section aria-label="Availability" style={{ fontFamily: font.family }}>
      <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: space.md }}>
        {/* Doctors by call order */}
        <Card style={{ flex: 1 }}>
          <h2 style={st.heading}>On Call Today</h2>
          {sortedDoctors.length === 0 ? (
            <p style={st.empty}>No doctors on the list.</p>
          ) : (
            <ul style={st.list}>
              {sortedDoctors.map((d) => {
                const isDeparted = !!departed[d.id];
                return (
                  <li key={d.id} style={{ ...st.row, ...(isDeparted ? st.departedRow : null) }}>
                    <span style={st.callOrderPill} aria-hidden="true">
                      {callOrderLabel(d.callOrder)}
                    </span>
                    {isDeparted ? <DepartedName name={d.name} /> : <span style={st.name}>{d.name}</span>}
                    {d.id === aicId && <AicPill />}
                    {renderActions && !isDeparted && (
                      <span style={{ marginLeft: "auto" }}>{renderActions(d)}</span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </Card>

        {/* CRNAs by go-home time — soonest to leave first */}
        <Card style={{ flex: 1 }}>
          <h2 style={st.heading}>Going Home</h2>
          {sortedCrnas.length === 0 ? (
            <p style={st.empty}>No CRNAs on the list.</p>
          ) : (
            <ul style={st.list}>
              {sortedCrnas.map((c) => {
                const isDeparted = !!departed[c.id];
                return (
                  <li key={c.id} style={{ ...st.row, ...(isDeparted ? st.departedRow : null) }}>
                    <span style={st.clockPill} aria-hidden="true">
                      {c.goHomeHour != null ? c.goHomeHour : "—"}
                    </span>
                    {isDeparted ? <DepartedName name={c.name} /> : <span style={st.name}>{c.name}</span>}
                    {!isDeparted && <GoHomeAlert goHomeHour={c.goHomeHour} now={now} />}
                    {renderActions && !isDeparted && (
                      <span style={{ marginLeft: "auto" }}>{renderActions(c)}</span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </div>
    </section>
  );
}

const st = {
  heading: {
    margin: `0 0 ${space.md}px`,
    fontSize: font.size.heading,
    fontWeight: font.weight.bold,
    color: color.text.primary,
    lineHeight: font.lineHeight.tight,
  },
  list: {
    listStyle: "none",
    margin: 0,
    padding: 0,
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: space.sm,
    padding: `${space.sm}px 0`,
    borderBottom: `1px solid ${color.border.default}`,
    minHeight: 36,
  },
  departedRow: {
    opacity: 0.45,
  },
  name: {
    fontSize: font.size.body,
    color: color.text.primary,
  },
  departedName: {
    fontSize: font.size.body,
    color: color.text.primary,
  },
  departedLabel: {
    fontSize: font.size.label,
    color: color.text.muted,
  },
  // Navy call-order badge per UI-SPEC: 20×20 pill, white 12/700.
  callOrderPill: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 20,
    height: 20,
    borderRadius: radius.full,
    background: color.accent.primary,
    color: "#ffffff",
    fontSize: font.size.label,
    fontWeight: font.weight.bold,
    flexShrink: 0,
  },
  // Clock-hour badge per UI-SPEC: 28×20 pill on raised surface.
  clockPill: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 28,
    height: 20,
    borderRadius: radius.full,
    background: color.surface.raised,
    color: color.text.secondary,
    border: `1px solid ${color.border.default}`,
    fontSize: font.size.label,
    fontWeight: font.weight.bold,
    flexShrink: 0,
  },
  aicPill: {
    background: "#fef8ec",
    color: "#7a4f00",
    border: `1px solid ${color.accent.closing}`,
    borderRadius: radius.sm,
    padding: "1px 6px",
    fontSize: font.size.label,
    fontWeight: font.weight.bold,
  },
  empty: {
    textAlign: "center",
    fontSize: font.size.body,
    color: color.text.muted,
    margin: `${space.md}px 0`,
  },
};
